import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GtwSnackbarService } from '../../shared/_services/gtw-snackbar.service';
import { Process5713LinesService } from './process-5713-lines.service';
import { GtwElementsBaseUrlsModel } from '../../shared/_models/gtw-elements-base-urls.model';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { AppService } from '../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
} from '../../shared/workspace-element-context';

@Component({
    selector: 'gtw-process-5713-lines',
    templateUrl: './process-5713-lines.component.html',
    styleUrls: ['./process-5713-lines.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class Process5713LinesComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private subs = new Subscription();

  filterParams: any = {};
  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};

  // Component properties
  modalTitle = "Process 5713 Lines";
  minimise = false;
  saveloader = false;
  tags: string[] = [];
  
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};

  constructor(
    private gtwSnackbarService: GtwSnackbarService,
    private process5713LinesService: Process5713LinesService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext()));
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext()));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.baseURLs = {
      api: gc.api,
      admin: gc.admin,
      custom: gc.custom,
      efile: gc.efile,
      pdfEngine: gc.pdfEngine,
      bulkPdfEngine: gc.bulkPdfEngine,
    };
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.USER_SETTINGS = shell.userSettingsData;
    this.SERVER_CONFIG = shell.serverConfigData;
    this.GENERAL_CONFIG = shell.generalConfigData;
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.filterParams = normalizeFilterParams(activeScreen);
    this.init();
    this.cdr.markForCheck();
  }

  init(): void {
    // Check if all required data is available
    if (this.baseURLs && Object.keys(this.baseURLs).length > 0 && 
        this.filterParams && Object.keys(this.filterParams).length > 0 &&
        this.GENERAL_CONFIG && Object.keys(this.GENERAL_CONFIG).length > 0) {
      this.initializeData();
    }
  }

  initializeData(): void {
    // Initialize tags from filterParams (like AngularJS controller)
    if (this.filterParams.submission_id) {
      this.tags = [this.filterParams.submission_id];
    }
    this.cdr.detectChanges();
  }

  process5713LinesData(): void {
    // Prevent double-click
    if (this.saveloader) {
      return;
    }

    this.saveloader = true;
    this.cdr.detectChanges();

    // Build params exactly like AngularJS controller
    const params = {
      "tax_year": this.filterParams.tax_year,
      "submission_id": this.filterParams.submission_id
    };

    // Call service method
    this.process5713LinesService.process5713Lines(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.saveloader = false;
        this.cdr.detectChanges();

        if (response.callSuccess === "1") {
          // Show success message (like AngularJS AlertService.add)
          this.gtwSnackbarService.openSnackBar('Successfully processed the 5713 lines data.', 'gtw-snackbar--green');
          this.cancel();
        } else {
          // Show error message (like AngularJS AlertService.add)
          this.gtwSnackbarService.openSnackBar(response.errorMessage || 'Error processing 5713 lines data.', 'gtw-snackbar--red');
        }
      },
      (error: any) => {
        this.saveloader = false;
        this.cdr.detectChanges();
        this.gtwSnackbarService.openSnackBar('Error occurred while processing. Please try again.', 'gtw-snackbar--red');
      }
    );
  }

  removeTag(index: number): void {
    this.tags.splice(index, 1);
    this.cdr.detectChanges();
  }

  cancel(): void {
    // Following pattern - dispatch CustomEvent to close modal
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true
    });
    
    if (document.querySelector('gtw-process-5713-lines-ce')) {
      document.querySelector('gtw-process-5713-lines-ce')?.dispatchEvent(updateEvent);
    }
  }
}

